import { useQuery } from '@apollo/client';
import { CircularProgress } from '@elipssolution/harfang';
import { Stack, styled } from '@mui/material';
import { addMonths, addYears, format, getDaysInMonth } from 'date-fns';
import { fr } from 'date-fns/locale';
import numeral from 'numeral';
import { useMemo, useState } from 'react';

import BarChart from './charts/BarChart';
import LineChart from './charts/LineChart';
import { WidgetRevenueConfigurationType } from './configuration/WidgetRevenueConfiguration';
import PeriodBanner from './PeriodBanner';
import { shortMonthsName } from './utils/shortMonthsName';
import { FETCH_REVENUE, FetchRevenueType } from '../../../../src/api/revenue';
import { ChartTypeEnum, PeriodicityEnum } from '../../../../src/types/revenue';
import { WidgetComponentProps } from '../../../../types/widget';

const TODAY_DATE = new Date();

const LoadingWrapper = styled('div')({
	position: 'absolute',
	inset: 0,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const generateFakeData = (
	periodicity: PeriodicityEnum,
	date: Date,
): {
	items: {
		value: number;
		previousValue: number;
		name: string;
	}[];
	balance: string;
	previousBalance: string;
} => {
	const period =
		periodicity === PeriodicityEnum.ANNUALLY
			? shortMonthsName
			: Array.from({ length: getDaysInMonth(date) }, (_, i) => (i + 1).toString());

	const items = period.map((periodItem) => ({
		name: periodItem,
		previousValue: Math.random(),
		value: Math.random(),
	}));

	const { balance, previousBalance } = (
		items as {
			name: string;
			previousValue: number;
			value: number;
		}[]
	).reduce(
		(acc, { value, previousValue }) => {
			acc.balance = acc.balance.add(value || '0');
			acc.previousBalance = acc.previousBalance.add(previousValue || '0');

			return acc;
		},
		{ balance: numeral('0'), previousBalance: numeral('0') },
	);

	return {
		items,
		balance: balance.format('0,00'),
		previousBalance: previousBalance.format('0,00'),
	};
};

type DesktopWidgetRevenueProps = {
	configuration: WidgetComponentProps['configuration'];
	readOnly: WidgetComponentProps['readOnly'];
};

const DesktopWidgetRevenue = ({ configuration, readOnly }: DesktopWidgetRevenueProps) => {
	const {
		accountRanges,
		isBalanceCumulative = false,
		periodicity = PeriodicityEnum.ANNUALLY,
		chartType = ChartTypeEnum.BAR_CHART,
		formula,
	} = (configuration as WidgetRevenueConfigurationType | undefined) ?? {};

	const [date, setDate] = useState(TODAY_DATE);

	const handleDateChange = (shift: number) =>
		setDate(periodicity === PeriodicityEnum.ANNUALLY ? addYears(date, shift) : addMonths(date, shift));

	const { data: revenueData, loading: isRevenueLoading } = useQuery<FetchRevenueType>(FETCH_REVENUE, {
		skip: !periodicity || !date,
		variables: {
			revenueInput: {
				rangeAccount: accountRanges?.map(({ value }) => value),
				isBalanceCumulative,
				date,
				periodicity,
				formula,
			},
		},
	});

	const {
		revenue: { balance = '0', data = [{ name: '', previousValue: 0, value: 0 }], previousBalance = '0' },
	} = revenueData || { revenue: {} };

	const fakeData = useMemo(() => generateFakeData(periodicity, date), [periodicity, date]);

	const revenues = useMemo(
		() =>
			readOnly
				? fakeData.items
				: data?.map(({ name, previousValue, value }) => ({
						name: periodicity === PeriodicityEnum.ANNUALLY ? shortMonthsName[+name - 1] : name,
						previousValue: +previousValue,
						value: +value,
				  })) || [],
		[data, fakeData.items, periodicity, readOnly],
	);

	const labelFormatter = (label: string) =>
		`${label}${
			periodicity === PeriodicityEnum.MONTHLY
				? ` ${format(date, 'MMMM', {
						locale: fr,
				  })}`
				: ''
		}`;

	const renderChart = () => {
		switch (chartType) {
			case ChartTypeEnum.LINE_CHART:
				return <LineChart data={revenues} labelFormatter={labelFormatter} />;
			default:
				return <BarChart data={revenues} labelFormatter={labelFormatter} />;
		}
	};

	return (
		<Stack flex={1} position="relative">
			{isRevenueLoading ? (
				<LoadingWrapper>
					<CircularProgress color="inherit" />
				</LoadingWrapper>
			) : (
				<>
					{renderChart()}

					<PeriodBanner
						balance={readOnly ? fakeData.balance : balance}
						date={date}
						periodicity={periodicity}
						onDateChange={handleDateChange}
						previousBalance={readOnly ? fakeData.previousBalance : previousBalance}
					/>
				</>
			)}
		</Stack>
	);
};

export default DesktopWidgetRevenue;
